import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { AuthStateFacade, StoreErrorHandler } from '@zero/shared/util-global-state';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { GuidedTourInfrastructure } from '../infrastructure/guided-tour.infrastructure';
import { GuidedTourActions, GuidedTourApiActions } from './guided-tour.actions';
import { guidedTourFeature } from './guided-tour.reducer';

@Injectable()
export class GuidedTourEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly guidedTourInfrastructure = inject(GuidedTourInfrastructure);
  private readonly authStateFacade = inject(AuthStateFacade);
  private readonly storeErrorHandler = inject(StoreErrorHandler);

  public loadGuidedTourNotifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GuidedTourActions.loadGuidedTourNotifications),
      concatLatestFrom(() => this.store.select(guidedTourFeature.selectLoaded)),
      filter(([, loaded]) => !loaded && Boolean(this.authStateFacade.customerId)),
      tap(() => GuidedTourActions.setGuidedTourLoading({ loading: true })),
      switchMap(() =>
        this.guidedTourInfrastructure.getShowNotifications().pipe(
          map(({ showNotification }) => GuidedTourApiActions.loadGuidedTourNotificationsSuccess({ showNotification })),
          catchError((error: HttpErrorResponse) => of(GuidedTourApiActions.loadGuidedTourNotificationsFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(GuidedTourApiActions.loadGuidedTourNotificationsFailure({ error }))),
    );
  });

  public dismissGuidedTourNotification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GuidedTourActions.dismissGuidedTourNotification),
      switchMap(() =>
        this.guidedTourInfrastructure.putDismissNotification().pipe(
          map(() => GuidedTourApiActions.dismissGuidedTourNotificationSuccess()),
          catchError((error: HttpErrorResponse) => of(GuidedTourApiActions.dismissGuidedTourNotificationFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(GuidedTourApiActions.dismissGuidedTourNotificationFailure({ error }))),
    );
  });

  public handleGuidedTourBackendError$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(GuidedTourApiActions.loadGuidedTourNotificationsFailure, GuidedTourApiActions.dismissGuidedTourNotificationFailure),
        tap((error) => this.storeErrorHandler.handleBackendError(error)),
        tap(() => GuidedTourActions.setGuidedTourLoading({ loading: false })),
      );
    },
    { dispatch: false },
  );
}
